import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { formatGel, formatPoints } from '../lib/format';
import { useCustomers, useMergeCustomers, useMergePreview } from '../lib/queries';
import { useDebounced } from '../lib/useDebounced';
import type { MergeSide } from '../lib/queries';
import { Alert, Button, Card, Field, SectionTitle, TierBadge } from './ui';

function Side({ side, label }: { side: MergeSide; label: string }) {
  const { t } = useI18n();
  return (
    <div className="rounded-xl border border-line bg-raised/40 p-3">
      <div className="text-[0.62rem] font-semibold uppercase tracking-wider text-ink-faint">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold text-ink">
        {side.customer.firstName} {side.customer.lastName}
      </div>
      <div className="tabular mt-0.5 text-[0.68rem] text-ink-faint">
        {side.customer.loyaltyId} · {side.customer.phone}
      </div>
      <dl className="mt-2 space-y-0.5 border-t border-line pt-2 text-[0.7rem]">
        <div className="flex justify-between gap-2">
          <dt className="text-ink-faint">{t('merge.lifetime')}</dt>
          <dd className="tabular text-ink-dim">{formatGel(side.loyalty.lifetimeSpendTetri)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-ink-faint">{t('merge.cashback')}</dt>
          <dd className="tabular text-ink-dim">{formatGel(side.loyalty.cashbackAvailableTetri)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-ink-faint">{t('merge.points')}</dt>
          <dd className="tabular text-ink-dim">{formatPoints(side.loyalty.pointsBalance)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-ink-faint">{t('merge.records')}</dt>
          <dd className="tabular text-ink-dim">
            {side.counts.orders}o · {side.counts.vehicles}v · {side.counts.redemptions}r
          </dd>
        </div>
      </dl>
    </div>
  );
}

export function CustomerMerge({ targetId, targetName }: { targetId: string; targetName: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const debounced = useDebounced(search);
  const { data: results } = useCustomers(debounced);
  const { data: preview, isPending: previewPending } = useMergePreview(sourceId, targetId);
  const merge = useMergeCustomers();

  const errorText = useApiError();
  const error = merge.error instanceof ApiError ? merge.error : null;
  const candidates = (results?.items ?? []).filter((c) => c.id !== targetId).slice(0, 6);

  if (!open) {
    return (
      <Card>
        <SectionTitle>{t('merge.duplicates')}</SectionTitle>
        <p className="text-sm leading-relaxed text-ink-dim">{t('merge.intro')}</p>
        <Button variant="ghost" className="mt-3 w-full" onClick={() => setOpen(true)}>{t('merge.start')}</Button>
      </Card>
    );
  }

  return (
    <Card>
      <SectionTitle action={
        <Button variant="quiet" size="sm" onClick={() => { setOpen(false); setSourceId(null); }}>{t('common.cancel')}</Button>
      }>
        {t('merge.into', { name: targetName })}
      </SectionTitle>

      {error && <div className="mb-3"><Alert>{errorText(error) ?? error.message}</Alert></div>}

      {!sourceId && (
        <>
          <input
            type="search"
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('merge.findPlaceholder')}
            aria-label={t('merge.find')}
            className="w-full rounded-xl border border-line bg-raised px-3.5 py-2.5 text-ink placeholder:text-ink-faint focus:border-brand focus:outline-none"
          />
          <div className="mt-2 space-y-1.5">
            {candidates.map((candidate) => (
              <button
                key={candidate.id}
                onClick={() => setSourceId(candidate.id)}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-line bg-raised px-3 py-2 text-left transition-colors hover:border-brand/50"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm text-ink">{candidate.name}</span>
                  <span className="tabular block text-[0.68rem] text-ink-faint">
                    {candidate.phone} · {candidate.loyaltyId}
                  </span>
                </span>
                {candidate.tierCode && <TierBadge code={candidate.tierCode} size="sm" />}
              </button>
            ))}
            {debounced && candidates.length === 0 && (
              <p className="py-3 text-center text-xs text-ink-faint">{t('merge.nobodyMatches')}</p>
            )}
          </div>
        </>
      )}

      {sourceId && previewPending && <p className="text-sm text-ink-dim">{t('merge.working')}</p>}

      {sourceId && preview && (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            <Side side={preview.source} label={t('merge.foldedIn')} />
            <Side side={preview.target} label={t('merge.survives')} />
          </div>

          <div className="mt-3 rounded-xl border border-brand/35 bg-brand-wash px-4 py-3">
            <div className="text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-brand">{t('merge.result')}</div>
            <dl className="mt-2 space-y-1 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-dim">{t('merge.resultLifetime')}</dt>
                <dd className="tabular text-ink">{formatGel(preview.result.lifetimeSpendTetri)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-dim">{t('merge.resultCashback')}</dt>
                <dd className="tabular text-ink">{formatGel(preview.result.cashbackAvailableTetri)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-dim">{t('merge.resultPoints')}</dt>
                <dd className="tabular text-ink">{formatPoints(preview.result.pointsBalance)}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-dim">{t('merge.tier')}</dt>
                <dd><TierBadge code={preview.result.tierCode} size="sm" /></dd>
              </div>
            </dl>
          </div>

          <div className="mt-3">
            <Field
              label={t('common.reason')}
              placeholder={t('merge.reasonPlaceholder')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              hint={t('order.reasonAudited')}
            />
          </div>

          {!confirming ? (
            <Button
              variant="danger"
              className="mt-3 w-full"
              disabled={reason.trim().length < 3}
              onClick={() => setConfirming(true)}
            >{t('merge.confirm')}</Button>
          ) : (
            <div className="mt-3 rounded-xl border border-danger/35 bg-danger/10 p-4">
              <p className="text-sm font-semibold text-ink">{t('merge.confirmQuestion', { id: preview.source.customer.loyaltyId })}</p>
              <ul className="mt-2 space-y-1 text-xs text-ink-dim">
                <li>{t('merge.point1')}</li>
                <li>{t('merge.point2')}</li>
                <li>{t('merge.point3')}</li>
                <li>{t('merge.point4')}</li>
                <li>{t('merge.point5')}</li>
              </ul>
              <div className="mt-4 flex gap-2">
                <Button
                  variant="danger"
                  className="flex-1"
                  loading={merge.isPending}
                  onClick={() => merge.mutate({
                    sourceCustomerId: sourceId,
                    targetCustomerId: targetId,
                    reason: reason.trim(),
                  }, {
                    onSuccess: () => { setConfirming(false); setOpen(false); navigate(0); },
                  })}
                >{t('merge.do')}</Button>
                <Button variant="ghost" onClick={() => setConfirming(false)}>{t('common.cancel')}</Button>
              </div>
            </div>
          )}

          <button
            onClick={() => { setSourceId(null); setConfirming(false); }}
            className="mt-3 w-full text-center text-xs text-ink-faint transition hover:text-ink-dim"
          >{t('merge.pickAnother')}</button>
        </>
      )}
    </Card>
  );
}
